<template>
  <div class="cost-billing">
    <div class="billing-header">
      <h3>Billing</h3>
    </div>

    <div class="billing-stats">
      <!-- Package Information Card -->
      <div class="billing-card primary">
        <div class="billing-icon mdi mdi-package-variant-closed"></div>
        <div class="billing-content">
          <h3>Package</h3>
          <div class="billing-amount">
            {{ (packageData.package + 100).toLocaleString() }} messages
          </div>
          <div class="billing-details">
            Monthly allowance
          </div>
        </div>
      </div>

      <!-- Sent Messages Card -->
      <div class="billing-card">
        <div class="billing-icon mdi mdi-send-check-outline"></div>
        <div class="billing-content">
          <h3>Sent</h3>
          <div class="billing-amount">
            {{ packageData.sent.toLocaleString() }}
          </div>
          <div class="billing-details">
            {{ ((packageData.sent / packageData.package) * 100).toFixed(1) }}%
            of package used
          </div>
        </div>
      </div>

      <!-- Remaining Messages Card -->
      <div class="billing-card">
        <div class="billing-icon mdi mdi-counter"></div>
        <div class="billing-content">
          <h3>Remaining</h3>
          <div class="billing-amount">
            {{ packageData.remaining.toLocaleString() }}
          </div>
          <div class="billing-details">
            Messages left in package
          </div>
        </div>
      </div>

      <!-- Days Remaining Card -->
      <div class="billing-card" v-if="daysRemaining > 0">
        <div class="billing-icon mdi mdi-calendar-clock-outline"></div>
        <div class="billing-content">
          <h3>Days Left</h3>
          <div class="billing-amount">
            {{ daysRemaining }}
          </div>
          <div class="billing-details">
            Current billing cycle
          </div>
        </div>
      </div>
    </div>

    <!-- Usage Progress Bar -->
    <div class="usage-progress">
      <div class="progress-header">
        <h4>Package usage</h4>
        <span class="usage-percentage">{{ usagePercentage }}%</span>
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: usagePercentage + '%' }"
          :class="{
            warning: usagePercentage > 80,
            danger: usagePercentage > 95
          }"
        ></div>
      </div>
      <div class="progress-details">
        <span
          >{{ packageData.sent.toLocaleString() }} of
          {{ packageData.package.toLocaleString() }} messages used</span
        >
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CostBilling",
  props: {
    packageData: {
      type: Object,
      required: true,
      default: () => ({
        package: 0,
        sent: 0,
        remaining: 0,
        subscription_date: null
      })
    }
  },
  computed: {
    usagePercentage() {
      if (this.packageData.package === 0) return 0;
      return Math.round(
        (this.packageData.sent / this.packageData.package) * 100
      );
    },
    daysRemaining() {
      if (!this.packageData.subscription_date) return 0;

      const today = new Date();
      const subDate = new Date(this.packageData.subscription_date);

      // Calculate the end of the month for the subscription date
      const endOfMonth = new Date(
        subDate.getFullYear(),
        subDate.getMonth() + 1,
        0
      );

      // Calculate days remaining
      const timeDiff = endOfMonth.getTime() - today.getTime();
      const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

      return Math.max(0, daysRemaining);
    }
  }
};
</script>

<style scoped>
.cost-billing {
  padding: 0;
}

.billing-header h3 {
  margin: 0 0 14px;
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
}

.billing-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.billing-card {
  background: var(--surface);
  padding: 14px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  display: flex;
  align-items: center;
  gap: 12px;
}

.billing-card.primary {
  background: var(--surface);
  border-color: rgba(109, 84, 199, 0.32);
  color: var(--text);
}

.billing-icon {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  background: var(--accent-050);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-600);
  font-size: 18px;
}

.billing-card:not(.primary) .billing-icon {
  background: var(--surface-2);
}

.billing-content h3 {
  margin: 0 0 3px;
  font-size: var(--fs-sm);
  color: var(--muted);
  font-weight: 600;
}

.billing-amount {
  color: var(--ink-900);
  font-size: var(--fs-h1);
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 3px;
}

.billing-details {
  font-size: var(--fs-xs);
  color: var(--muted);
}

.usage-progress {
  background: var(--surface);
  padding: 16px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  margin-bottom: 16px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-header h4 {
  margin: 0;
  color: var(--ink-900);
  font-size: var(--fs-body);
  font-weight: 700;
}

.usage-percentage {
  font-size: var(--fs-body);
  font-weight: 700;
  color: var(--accent-600);
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--line);
  border-radius: var(--radius-sm);
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: var(--success);
  border-radius: var(--radius-sm);
  transition: width 0.3s var(--ease);
}

.progress-fill.warning {
  background: var(--warning);
}

.progress-fill.danger {
  background: var(--danger);
}

.progress-details {
  color: var(--muted);
  font-size: var(--fs-sm);
}

@media screen and (max-width: 1180px) {
  .billing-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 640px) {
  .billing-stats {
    grid-template-columns: 1fr;
  }
}
</style>
